import { FormikErrors, useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Button, FormControl, FormHelperText, InputLabel, OutlinedInput, Stack, Typography } from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { IDistributionList, IPostDistributionListIndividualContacts } from '../../../../shared/services/distribution-list/distribution-list.model';
import DistributionListService from '../../../../shared/services/distribution-list/distribution-list.service';
import { IPostDistributionListAddress } from '../../../../shared/services/distribution-list-address/distribution-list-address.model';
import DistributionListAddressService from '../../../../shared/services/distribution-list-address/distribution-list-address.service';
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';

export const DistributionListIndividualContacts: FunctionComponent<{
  newDistributionList: IPostDistributionListIndividualContacts,
  closeHandler: () => void,
  successHandler: () => void,
  distributionListDetails?: IDistributionList,
  selectedUser?: { id: string, name: string, email: string },
}> = ({
  closeHandler,
  successHandler,
  newDistributionList,
  distributionListDetails,
  selectedUser,
}) => {
    const userNotificationsContext = useContext(UserNotificationsContext)
    const distributionListService = new DistributionListService()
    const distributionListAddressService = new DistributionListAddressService()
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const distributionListSchema = Yup.object().shape({
      listName: Yup.string().required('List Name is required'),
      emailAddresses: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required('Name is required'),
          emailAddress: Yup.string().email('Invalid email address').required('Email is required')
        })
      )
    });

    const formik = useFormik({
      initialValues: {
        listName: newDistributionList.listName,
        isGenericTeamEmail: newDistributionList.isGenericTeamEmail,
        emailAddresses: newDistributionList.emailAddresses,
      },
      validationSchema: distributionListSchema,
      onSubmit: async (values) => {
        if (distributionListDetails) {
          if (!isSubmitting) {
            setIsSubmitting(true)
            if (selectedUser && selectedUser.id !== '') {
              distributionListAddressService.editDistributionListAddress(selectedUser.id, { emailAddresses: values.emailAddresses, distributionListId: distributionListDetails.id })
                .then(response => {
                  formik.resetForm()
                  successHandler()
                  setIsSubmitting(false)
                })
                .catch(err => {
                  setIsSubmitting(false)
                  userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  console.log(err)
                })
            } else {
              distributionListAddressService.addDistributionListAddress({ emailAddresses: values.emailAddresses, distributionListId: distributionListDetails.id })
                .then(response => {
                  successHandler()
                  formik.resetForm()
                  setIsSubmitting(false)
                })
                .catch(err => {
                  setIsSubmitting(false)
                  userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  console.log(err)
                })
            }
          }
        } else {
          const isValid = await formik.validateForm();
          if (Object.keys(isValid).length === 0) {
            if (!isSubmitting) {
              setIsSubmitting(true)
              distributionListService.createDistributionList(values)
                .then(response => {
                  successHandler()
                  formik.resetForm()
                  setIsSubmitting(false)
                })
                .catch(err => {
                  setIsSubmitting(false)
                  userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  console.log(err)
                })
            }

          }
        }
      },
      validateOnChange: false,
      validateOnBlur: false,
    });

    useEffect(() => {
      if (distributionListDetails) {
        formik.setFieldValue('listName', distributionListDetails.listName)
      }
      if (selectedUser) {
        formik.setFieldValue('emailAddresses', [{ name: selectedUser.name, emailAddress: selectedUser.email }]);
      }
    }, [])

    const addEmailField = async () => {
      const errors = await formik.validateForm();
      const hasListNameError = !!errors.listName;
      const hasEmailAddressesError = formik.values.emailAddresses.some(
        (address, index) => errors.emailAddresses && errors.emailAddresses[index]
      );
      if (!hasListNameError && !hasEmailAddressesError) {
        formik.setFieldValue('emailAddresses', [
          ...formik.values.emailAddresses,
          { name: '', emailAddress: '' }
        ]);
      } else {
        formik.setTouched({
          listName: true,
          emailAddresses: formik.values.emailAddresses.map(() => ({
            name: true,
            emailAddress: true,
          })),
        });
      }
    };

    const removeLastEmailField = () => {
      const updatedEmailAddresses = [...formik.values.emailAddresses];
      updatedEmailAddresses.pop();
      formik.setFieldValue('emailAddresses', updatedEmailAddresses);
    };

    const handleEmailAddressChange = (index: number, field: keyof IPostDistributionListAddress, value: string) => {
      const updatedEmailAddresses = [...formik.values.emailAddresses];
      updatedEmailAddresses[index] = {
        ...updatedEmailAddresses[index],
        [field]: value
      };
      formik.setFieldValue('emailAddresses', updatedEmailAddresses);
    };

    const isEmailAddressErrorsArray = (
      errors: typeof formik.errors.emailAddresses
    ): errors is FormikErrors<IPostDistributionListAddress>[] => {
      return Array.isArray(errors);
    };

    const cancelHandler = () => {
      closeHandler();
      formik.resetForm()
    };

    const isLastFieldEmpty = formik.values.emailAddresses.length > 1 &&
      !formik.values.emailAddresses[formik.values.emailAddresses.length - 1].name &&
      !formik.values.emailAddresses[formik.values.emailAddresses.length - 1].emailAddress;

    return (
      <Box >
        <Box>
          {!distributionListDetails && <FormControl fullWidth variant='outlined' sx={{ mt: 2, mb: 2 }}>
            <InputLabel htmlFor='listName'>Distribution List Name</InputLabel>
            <OutlinedInput
              id='listName'
              name='listName'
              type='text'
              label='Distribution List Name'
              value={formik.values.listName}
              onChange={formik.handleChange}
              error={formik.touched.listName && Boolean(formik.errors.listName)}
            />
            {formik.touched.listName && formik.errors.listName && (
              <FormHelperText sx={{ color: "#bf3333", ml: 2 }}>{formik.errors.listName}</FormHelperText>
            )}
          </FormControl>}
          {formik.values.emailAddresses.map((email, index) => {
            return (
              <Box key={index} sx={{ mb: 2 }}>
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor={`name-${index}`}>Name</InputLabel>
                  <OutlinedInput
                    id={`name-${index}`}
                    name={`emailAddresses[${index}].name`}
                    type='text'
                    label='Name'
                    value={email.name}
                    onChange={(e) => handleEmailAddressChange(index, 'name', e.target.value)}
                    error={Boolean(
                      isEmailAddressErrorsArray(formik.errors.emailAddresses) &&
                      formik.errors.emailAddresses[index]?.name
                    )}
                  />
                  {isEmailAddressErrorsArray(formik.errors.emailAddresses) &&
                    formik.errors.emailAddresses[index]?.name && (
                      <FormHelperText sx={{ color: "#bf3333", ml: 2 }}>
                        {formik.errors.emailAddresses[index]?.name}
                      </FormHelperText>
                    )}
                </FormControl>
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor={`emailAddress-${index}`}>Email Address</InputLabel>
                  <OutlinedInput
                    id={`emailAddress-${index}`}
                    name={`emailAddresses[${index}].emailAddress`}
                    type='email'
                    label='Email Address'
                    value={email.emailAddress}
                    onChange={(e) => handleEmailAddressChange(index, 'emailAddress', e.target.value)}
                    error={Boolean(
                      isEmailAddressErrorsArray(formik.errors.emailAddresses) &&
                      formik.errors.emailAddresses[index]?.emailAddress
                    )}
                  />
                  {isEmailAddressErrorsArray(formik.errors.emailAddresses) &&
                    formik.errors.emailAddresses[index]?.emailAddress && (
                      <FormHelperText sx={{ color: "#bf3333", ml: 2 }}>
                        {formik.errors.emailAddresses[index]?.emailAddress}
                      </FormHelperText>
                    )}
                </FormControl>
              </Box>
            )
          })}
          {(!distributionListDetails || (selectedUser && selectedUser.id === '')) && <Box display="flex" justifyContent="flex-start" mt={2}>
            {isLastFieldEmpty ? (
              <Button onClick={removeLastEmailField} variant='outlined' disabled={isSubmitting}>
                <Remove />
                <Typography>Remove Field</Typography>
              </Button>
            ) : (
              <Button onClick={addEmailField} variant='outlined' disabled={isSubmitting}>
                <Add />
                <Typography>Add More</Typography>
              </Button>
            )}
          </Box>}
        </Box>
        <Stack display='flex' flexDirection='row' justifyContent='flex-end' alignItems='center' gap={1}>
          <DialogSaveButtons
            cancelHandler={cancelHandler}
            submitHandler={formik.submitForm}
            isSubmitting={isSubmitting}
          />
        </Stack>
      </Box>
    );
  };